import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, CopyButton, Group, SimpleGrid, Stack, Text, Title } from '@repletoryreach/sdk-ui';
import { DataTable } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { formatCount } from '../../../lib/format';
import type { Indexed } from '../../../lib/types';
import { CorrectionDrawer, type CorrectionDraft } from '../../knowledge/Accuracy';
import { unansweredThemes, type ThemeStat } from '../lib/data';
import { Bullets, MUTED, Muted, Panel, SeeChats, WholeReport } from '../shared';
import type { TabProps } from './tab';

/**
 * What the chatbot could not answer: the questions by theme with the customers' own words, each with Answer this (the
 * knowledge base's correction form, filled in with the question), what the AI says is missing and why, and the
 * documents it suggests adding next, each with an outline to copy.
 */
export function Gaps({ report, rows, onSee }: TabProps) {
  const t = useT();
  const [draft, setDraft] = useState<CorrectionDraft | null>(null);
  const themes = unansweredThemes(rows);
  const s = report.sections;

  return (
    <Stack gap="lg">
      <Panel title={t('insights.gaps.title')} note={t('insights.gaps.note')} testId="insights-gaps">
        {themes.length === 0 ? <Muted>{t('insights.gaps.none')}</Muted> : (
          <div className="rr-insights__table-scroll">
            <DataTable<Indexed<ThemeStat>>
              records={themes as Indexed<ThemeStat>[]}
              idAccessor="theme"
              columns={[
                { accessor: 'theme', title: t('insights.gaps.theme'), render: (x) => (
                  <Stack gap={2}>
                    <Text role="bodySmall" fw={600}>{x.theme}</Text>
                    {x.examples.map((e) => <Text key={e} role="caption" c={MUTED}>“{e}”</Text>)}
                  </Stack>
                ) },
                { accessor: 'asked', title: t('insights.gaps.asked'), textAlign: 'right', render: (x) => formatCount(x.asked) },
                { accessor: 'chats', title: t('insights.topics.chats'), textAlign: 'right', render: (x) => formatCount(x.chats) },
                { accessor: 'refs', title: '', render: (x) => (
                  <Group gap={4} wrap="wrap" justify="flex-end">
                    <Button size="xs" variant="light" onClick={() => setDraft({ title: x.examples[0] ?? x.theme, answer: '' })} data-testid="insights-answer-this">
                      {t('insights.gaps.answer')}
                    </Button>
                    <SeeChats refs={x.refs} onSee={onSee} t={t} />
                  </Group>
                ) }
              ]}
            />
          </div>
        )}
        <Text role="caption" c={MUTED}>{t('insights.gaps.answerNote')}</Text>
        {/* Suggest flows with AI (30 October 2026), with these gaps as the ideas. */}
        {themes.length > 0 && (
          <Group>
            <Link to={`/flows/ideas?from=insights&report=${report.id}`} data-testid="insights-suggest-flows">
              <Button size="xs" variant="default">{t('insights.gaps.suggestFlows')}</Button>
            </Link>
          </Group>
        )}
      </Panel>

      {s && s.gaps.length > 0 && (
        <Panel title={t('insights.gaps.missing')} note={<WholeReport t={t} />}>
          <Stack gap="sm">
            {s.gaps.map((g, i) => (
              <Stack key={i} gap={2}>
                <Text role="bodySmall" fw={600}>{t('insights.gaps.missingLine', { theme: g.theme, count: formatCount(g.count) })}</Text>
                <Text role="bodySmall" c={MUTED}>{g.why}</Text>
                <SeeChats refs={g.refs} onSee={onSee} t={t} />
              </Stack>
            ))}
          </Stack>
        </Panel>
      )}

      {s && s.documents.length > 0 && (
        <Stack gap="sm" data-testid="insights-documents">
          <Title order={4}>{t('insights.gaps.documents')}</Title>
          <WholeReport t={t} />
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
            {s.documents.map((d, i) => {
              const outline = [d.title, '', ...d.outline.map((o) => `- ${o}`)].join('\n');
              return (
                <Card key={i} withBorder data-testid="insights-document">
                  <Stack gap="xs">
                    <Text role="bodySmall" fw={700}>{d.title}</Text>
                    <Text role="caption" c={MUTED}>{t('insights.gaps.wouldAnswer', { count: formatCount(d.count) })}</Text>
                    <Text role="bodySmall">{d.why}</Text>
                    <Title order={6}>{t('insights.gaps.outline')}</Title>
                    <Bullets items={d.outline} />
                    {d.answers.length > 0 && (
                      <>
                        <Title order={6}>{t('insights.gaps.questionsItAnswers')}</Title>
                        <Bullets items={d.answers.map((a) => `“${a}”`)} />
                      </>
                    )}
                    <Group gap="xs" wrap="wrap">
                      <CopyButton value={outline}>
                        {({ copied, copy }) => (
                          <Button size="xs" variant="default" onClick={copy}>{copied ? t('insights.gaps.copied') : t('insights.gaps.copy')}</Button>
                        )}
                      </CopyButton>
                      <Link to="/knowledge?tab=documents"><Button size="xs" variant="light">{t('insights.gaps.addDocument')}</Button></Link>
                      <SeeChats refs={d.refs} onSee={onSee} t={t} />
                    </Group>
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Stack>
      )}

      <CorrectionDrawer draft={draft} onClose={() => setDraft(null)} t={t} />
    </Stack>
  );
}
